import React, { useState, useMemo } from 'react';
import { Icons } from './Icon.jsx';
import { getNodeChildren } from '../utils.js';
import ContextMenu from './ContextMenu.jsx';
import HierarchyTreeItem from './HierarchyTreeItem.jsx';

const HierarchyPanel = ({ 
    nodes, 
    selectedNodeIds, 
    onSelect, 
    onMove, 
    onUpdate, 
    onContextMenuAction, 
    onFocusItem, 
    rootId,
    collapsedNodeIds,
    onToggleNodeExpansion
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [contextMenu, setContextMenu] = useState(null);

  // Filtering Logic
  // If search is active, we don't need includedNodeIds for tree traversal since we use flat list
  // But we might want to keep it if we revert to tree
  const includedNodeIds = useMemo(() => {
    if (!searchTerm.trim()) return null;
    
    const searchTerms = searchTerm.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    if (searchTerms.length === 0) return null;

    const matchingNodes = nodes.filter(n => {
        const label = (n.label || '').toLowerCase();
        return searchTerms.some(term => label.includes(term));
    });

    const included = new Set();

    matchingNodes.forEach(match => {
        included.add(match.id);
        // Add all ancestors to ensure path is visible
        let curr = match;
        // If isolated, stop at rootId
        while(curr.parentId && curr.id !== rootId) {
            const parent = nodes.find(n => n.id === curr.parentId);
            if (!parent) break;
            included.add(parent.id);
            curr = parent;
        }
    });
    return included;
  }, [nodes, searchTerm, rootId]);

  // Flatten logic for range selection
  // If searching, flatList is just the matching nodes
  const flatList = useMemo(() => {
      if (searchTerm.trim()) {
           const searchTerms = searchTerm.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
           if (searchTerms.length === 0) return [];
           
           return nodes
              .filter(n => {
                  const label = (n.label || '').toLowerCase();
                  return searchTerms.some(term => label.includes(term));
              })
              .map(n => n.id);
      }

      const list = [];
      const traverse = (parentId) => {
          // Filter if search is active (but here we are in non-search branch ideally, 
          // though if searchTerm is present but we fell through? No, see if check above)
          const children = getNodeChildren(nodes, parentId); // No filtering needed for standard tree
          for (const child of children) {
              list.push(child.id);
              traverse(child.id);
          }
      };
      
      // Start traversal from rootId or undefined
      if (rootId && nodes.some(n => n.id === rootId)) {
          list.push(rootId);
          traverse(rootId);
      } else {
          traverse(undefined);
      }
      return list;
  }, [nodes, searchTerm, rootId]);

  // Get matching nodes for Search Mode (Flat)
  const matchingNodes = useMemo(() => {
      if (!searchTerm.trim()) return [];
      
      const searchTerms = searchTerm.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      if (searchTerms.length === 0) return [];
      
      return nodes.filter(n => {
          const label = (n.label || '').toLowerCase();
          return searchTerms.some(term => label.includes(term));
      });
  }, [nodes, searchTerm]);

  // Get root nodes (no parent) for Tree Mode
  // Respect isolation: if rootId exists in nodes, it is the only root
  const rootNodes = useMemo(() => {
      if (rootId) {
          const isolatedRoot = nodes.find(n => n.id === rootId);
          if (isolatedRoot) return [isolatedRoot];
      }
      return getNodeChildren(nodes, undefined);
  }, [nodes, rootId]);

  // Decide what to display
  const displayNodes = searchTerm.trim() ? matchingNodes : rootNodes;
  const isSearchMode = !!searchTerm.trim();

  const handleContextMenu = (e, node) => {
      setContextMenu({
          x: e.clientX,
          y: e.clientY,
          node
      });
  };

  return (
    <div className="flex flex-col w-full h-full">
      {/* Search Bar */}
      <div className="p-2 border-b border-admin_bordered bg-admin_canvas sticky top-0 z-10">
        <div className="relative">
          <Icons.Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-3 h-3" />
          <input 
            type="text" 
            placeholder="Search hierarchy..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-admin_paper border border-admin_bordered rounded pl-7 pr-2 py-1 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-admin_text"
          />
          {searchTerm && (
            <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
                <Icons.Remove className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      </div>

      <div 
          className="flex-1 flex flex-col w-full text-sm select-none py-2 overflow-y-auto"
          onDragOver={(e) => { e.preventDefault(); }} // Allow drop on empty area
          onDrop={(e) => {
              e.preventDefault();
              const dragId = e.dataTransfer.getData('nodeId');
              if (dragId) {
                  let idsToMove = [dragId];
                  if (selectedNodeIds.includes(dragId)) {
                      idsToMove = selectedNodeIds;
                  }
                  // Drop on empty area means move to root
                  onMove(idsToMove, null, 'inside');
              }
          }}
      >
        {nodes.length === 0 && (
          <div className="px-4 py-8 text-center text-xs text-gray-500 italic">
              Scene is empty.<br/>Switch to 'Components' to add items.
          </div>
        )}

        {displayNodes.length === 0 && searchTerm && (
            <div className="px-4 py-4 text-center text-xs text-gray-400 italic">
                No items match "{searchTerm}"
            </div>
        )}
        
        {displayNodes.map(node => (
          <HierarchyTreeItem 
            key={node.id} 
            node={node} 
            allNodes={nodes} 
            selectedNodeIds={selectedNodeIds} 
            onSelect={onSelect}
            onMove={onMove}
            onUpdate={onUpdate}
            onContextMenu={handleContextMenu}
            depth={0} // Always 0 depth for roots or flat list
            includedNodeIds={isSearchMode ? null : includedNodeIds} // Only needed for tree filtering, not used in flat mode
            searchTerm={searchTerm}
            flatList={flatList}
            onFocusItem={onFocusItem}
            showChildren={!isSearchMode} // Hide children in search mode
            collapsedNodeIds={collapsedNodeIds}
            onToggleNodeExpansion={onToggleNodeExpansion}
          />
        ))}

        {/* Helper text/area for dragging out */}
        {!searchTerm && (
            <div className="flex-1 min-h-[50px] flex items-center justify-center text-xs text-gray-600 border-t border-transparent hover:border-blue-500/30 hover:bg-white/5 transition-colors mt-2">
            Drop here to make Root
            </div>
        )}
      </div>

      {contextMenu && (
          <ContextMenu 
              x={contextMenu.x} 
              y={contextMenu.y} 
              items={[
                  { label: 'Group', onClick: () => onContextMenuAction('put-in-container', contextMenu.node) },
                  { label: 'Ungroup', onClick: () => onContextMenuAction('ungroup-container', contextMenu.node) }
              ]}
              onClose={() => setContextMenu(null)}
          />
      )}
    </div>
  );
};

export default HierarchyPanel;